/**
 * List row (§5.9): the whole row is one button or link; one optional trailing secondary action.
 * Min height 56px (72px with two lines).
 */
import type { ReactNode, Ref } from 'react';
import { Link } from '../app/Link';
import { cx } from './cx';
import { Icon } from './Icon';
import styles from './ListRow.module.css';

interface ListRowProps {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Third line (meta, status words). */
  meta?: ReactNode;
  trailing?: ReactNode;
  /** Keep the action column even when this row has no action, so trailing slots line up. */
  reserveAction?: boolean;
  /** Secondary action rendered outside the main button. */
  action?: ReactNode;
  onClick?(): void;
  to?: string;
  /** Full accessible name for the row's main control (defaults to its text). */
  ariaLabel?: string;
  selected?: boolean;
  chevron?: boolean;
  tone?: 'default' | 'accent' | 'danger' | 'muted' | 'ghost';
  className?: string;
  ref?: Ref<HTMLLIElement>;
  /** Tag for the outer element; rows normally live in a <ul role="list">. */
  as?: 'li' | 'div';
}

export function ListRow({
  leading,
  title,
  subtitle,
  meta,
  trailing,
  action,
  onClick,
  to,
  ariaLabel,
  selected,
  chevron = !!(onClick || to),
  reserveAction = false,
  tone = 'default',
  className,
  ref,
  as = 'li',
}: ListRowProps) {
  const content = (
    <>
      {leading && <span className={styles.leading}>{leading}</span>}
      <span className={styles.text}>
        <span className={styles.title}>{title}</span>
        {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        {meta && <span className={styles.meta}>{meta}</span>}
      </span>
      {trailing && <span className={styles.trailing}>{trailing}</span>}
      {chevron && (
        <span className={styles.chevron}>
          <Icon name="chevronRight" size={20} />
        </span>
      )}
    </>
  );
  const mainClass = cx(styles.main, (onClick || to) && styles.interactive);
  const Tag = as;
  return (
    <Tag
      ref={ref as Ref<HTMLLIElement & HTMLDivElement>}
      className={cx(styles.row, styles[tone], selected && styles.selected, className)}
    >
      {to ? (
        <Link
          to={to}
          className={mainClass}
          aria-label={ariaLabel}
          aria-current={selected ? 'true' : undefined}
        >
          {content}
        </Link>
      ) : onClick ? (
        <button
          type="button"
          className={mainClass}
          onClick={onClick}
          aria-label={ariaLabel}
          aria-current={selected ? 'true' : undefined}
        >
          {content}
        </button>
      ) : (
        <div className={mainClass}>{content}</div>
      )}
      {action ? (
        <span className={styles.action}>{action}</span>
      ) : (
        reserveAction && <span className={cx(styles.action, styles.actionSpacer)} aria-hidden="true" />
      )}
    </Tag>
  );
}

/** Section heading for lists, e.g. "Your armies". */
export function ListHeading({ children, count, id }: { children: ReactNode; count?: number; id?: string }) {
  return (
    <h3 className={styles.heading} id={id}>
      <span>{children}</span>
      {count !== undefined && <span className={styles.headingCount}>{count}</span>}
    </h3>
  );
}
